<script>
    import {onDestroy} from 'svelte';

    import {mapStore} from '@js/stores/mapStore';

    import I18n from '@js/modules/translations';

    import MapTileService from '@js/modules/maps/mapTileService';
    import AnalyticsService from '@js/modules/analyticsService';
    import {consumeFeatureUse} from '@js/modules/featureLimit';

    import {MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';

    import {ElevationRideTypes, HillyPlanningSpeedFactor} from '@js/components/modules/constants';

    import MapMultiDay from '@js/components/maps/MapMultiDay.svelte';
    import MapPoiSuggestions from '@js/components/maps/MapPoiSuggestions.svelte';

    const MapStageSummary = () => import('@js/components/maps/MapStageSummary.svelte');

    // boxed: the card of the planner's "Route options" block above it. showStages: false where the page
    // lists the daily stages itself (the planner, under the map).
    const {boxed = false, showStages = true} = $props();

    const PMAPS_STYLES = [MapTileService.layers.MAP_PLANNER, MapTileService.layers.MAP_PLANNER_OUTDOOR];
    // Prefer the store's published style (reactive to style switches) but fall back to the live map
    // interface, which always knows the current style. currentDistance is read as a reactive trigger
    // so this re-evaluates once the map/route is ready (the interface read isn't reactive on its own).
    const isPmapsStyle = $derived.by(() => {
        void $mapStore.currentDistance;
        const style = $mapStore.currentMapStyle || mapStore.map?._model?.mapStyleName;
        return PMAPS_STYLES.includes(style);
    });

    let poiSuggestions = $state.raw(null);
    let _suggesting = $state(false);

    let _expanded = $state(false);

    const stageSummary = $derived.by(() => {
        void $mapStore.currentDistance;
        void $mapStore.currentBreakTime;

        return mapStore.map?.getStageSummary ? mapStore.map.getStageSummary() : [];
    });

    // Drop stale suggestions once the route is cleared — on the map as well as in the panel.
    $effect(() => {
        if (!$mapStore.currentDistance) {
            poiSuggestions = null;
            mapStore.map?.clearSuggestedPoiMarkers?.();
        }
    });

    // The preview markers live on the map, not in this component: they must not outlive it.
    onDestroy(() => mapStore.map?.clearSuggestedPoiMarkers?.());

    const _handleGenerateStages = async (mode, target) => {
        if (!mapStore.map || !target) {
            return;
        }

        // Stage waypoints live in the RIDE_POIS module, deferred out of the initial map load.
        await MapBuilder.ensureModule(MAP_MODULES.RIDE_POIS);
        // RIDE_POIS just loaded → let RideCreation wire its POI callbacks so the batch adds are handled.
        window.dispatchEvent(new CustomEvent('mapplanner:poi-module-ready'));

        let stepMeters;
        if (mode === 'duration') {
            const rideType = $mapStore.currentRideType;
            const baseSpeed = parseFloat(window.averageRideTypeSpeed?.[rideType]) || 4;
            // Same hilly-terrain assumption as the round-trip generator: elevation activities
            // (hiking/bike) use a factor below the flat figure so stage lengths stay realistic on the
            // climbs typical of a multi-day trek rather than overshooting.
            const speedKmh = ElevationRideTypes.includes(rideType) ? baseSpeed * HillyPlanningSpeedFactor : baseSpeed;
            stepMeters = target * speedKmh * 1000;
        } else {
            stepMeters = target * (window.unit === 'mi' ? 1609.34 : 1000);
        }

        const splits = mapStore.map.computeStageSplits ? mapStore.map.computeStageSplits(stepMeters) : [];

        // Route shorter than one stage → nothing to place, so don't spend a free use or log a success.
        if (!splits.length) {
            return;
        }

        // Only now (a real result) gate the freemium use and record it. Stage waypoints skip the
        // per-waypoint cap so this isn't double-counted.
        if (!consumeFeatureUse('map_max_multi_day')) {
            return;
        }

        splits.forEach((split) => mapStore.map.addStageWaypoint(split));

        AnalyticsService.trackMultiDayGenerated();
    };

    // Added from its marker popup: the map already placed the waypoint and removed the marker, the
    // panel just drops it from the count.
    const _handleSuggestionPicked = (suggestion) => {
        poiSuggestions = (poiSuggestions || []).filter((entry) => entry !== suggestion);

        AnalyticsService.trackPoiSuggestionAdded();
    };

    const _handleSuggestPois = async () => {
        if (_suggesting) {
            return;
        }

        // Show the loading state immediately (before the module even loads) for instant feedback.
        _suggesting = true;
        let result;
        try {
            // POI suggestions live in the RIDE_POIS module, deferred out of the initial map load.
            await MapBuilder.ensureModule(MAP_MODULES.RIDE_POIS);
            window.dispatchEvent(new CustomEvent('mapplanner:poi-module-ready'));

            result = mapStore.map?.suggestPoisAlongRoute ? await mapStore.map.suggestPoisAlongRoute() : [];
        } finally {
            _suggesting = false;
        }

        // No POI near the route → show the empty state without spending a free use or logging a success.
        if (!result.length) {
            poiSuggestions = [];
            return;
        }

        // Real result → only now gate the freemium use (paywall if capped) and record it.
        if (!consumeFeatureUse('map_max_poi_suggestion')) {
            return;
        }

        poiSuggestions = result;
        // The map is the interface: the panel only counts what is shown on it.
        mapStore.map?.showSuggestedPoiMarkers?.(result, _handleSuggestionPicked);
        AnalyticsService.trackPoiSuggestionsUsed();
    };

    // Manual alternative to the auto split: arm the on-map waypoint mode (handled in BuildMap, which
    // owns the toolbar handler that loads RIDE_POIS + shows the placement hint).
    const _handleManualStages = () => {
        window.dispatchEvent(new CustomEvent('mapplanner:enable-waypoint-mode'));
    };
</script>

<div class="map-planning-tools" class:map-planning-tools-boxed={boxed} class:block-shadow-xl={boxed}>
    <button type="button"
            class="map-planning-tools-toggle"
            aria-expanded={_expanded}
            onclick={() => (_expanded = !_expanded)}>
        <span class="map-planning-tools-title">{I18n.t('js.maps.planning_tools.title')}</span>
        <svg class="map-planning-tools-caret" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"/>
        </svg>
    </button>

    {#if _expanded}
        <div class="map-planning-tools-body">
            {#if showStages && stageSummary.length > 1}
                {#await MapStageSummary() then StageSummaryLoader}
                    {@const StageSummary = StageSummaryLoader.default}
                    <StageSummary stages={stageSummary}/>
                {/await}
            {/if}

            <!-- Shown as available capabilities whenever the base condition holds, but disabled until a
                 route is drawn so the user discovers them up front. -->
            <MapMultiDay onGenerate={_handleGenerateStages}
                         onManualMode={_handleManualStages}
                         available={$mapStore.currentRideType === 'hiking'}
                         enabled={!!$mapStore.currentDistance}/>

            <MapPoiSuggestions suggestions={poiSuggestions}
                               loading={_suggesting}
                               onSuggest={_handleSuggestPois}
                               available={isPmapsStyle}
                               enabled={!!$mapStore.currentDistance}/>
        </div>
    {/if}
</div>
